import { getSupabase } from '../backend/supabaseAdapter';
import { signOutAction } from './sessionStore';

// Delete Account (Settings → Account & Security; App Store 5.1.1(v)). Knowledge re-expressed from
// the old backend protocol (delete-account-v2: prepare → commit → status), NOT the old client code:
//   - ONLY the v2 address is ever called. The v1 address deleted the caller on any request; it is
//     never spoken to, so a backend that has not deployed v2 answers 404 = "unavailable", zero deletion.
//   - prepare (user bearer) creates the server-side operation and destroys nothing;
//   - commit (capability in the body) is the only destructive step; its answer is classified and an
//     "unknown" (transport loss) is settled by one status query, never guessed.
//   - Local inspection data on this device is NOT removed by account deletion (it is the inspector's
//     working data; deleting an inspection is a separate, explicit action).
export type DeletionOutcome =
  | { kind: 'committed' }
  | { kind: 'unauthenticated' }
  | { kind: 'unavailable'; status: number | null }
  | { kind: 'denied' }
  | { kind: 'unknown' };

const DELETION_ENDPOINT_PATH = '/functions/v1/delete-account-v2';

function endpoint(): string {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').replace(/\/$/, '');
  return `${base}${DELETION_ENDPOINT_PATH}`;
}

async function call(body: Record<string, unknown>, bearer: string): Promise<{ kind: 'answered'; status: number; json: Record<string, unknown> | null } | { kind: 'unreachable' }> {
  const anon = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
  let res: Response;
  try {
    res = await fetch(endpoint(), {
      method: 'POST',
      headers: { Authorization: `Bearer ${bearer}`, apikey: anon, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch {
    return { kind: 'unreachable' };
  }
  const json = await res
    .json()
    .then((value: unknown) => (value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null))
    .catch(() => null);
  return { kind: 'answered', status: res.status, json };
}

export async function deleteAccountAction(): Promise<DeletionOutcome> {
  const client = getSupabase();
  if (!client) return { kind: 'unavailable', status: null };
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token ?? '';
  if (!token) return { kind: 'unauthenticated' };

  const prepared = await call({ action: 'prepare' }, token);
  if (prepared.kind === 'unreachable') return { kind: 'unavailable', status: null };
  if (prepared.status === 401) return { kind: 'unauthenticated' };
  const operationId = typeof prepared.json?.operationId === 'string' ? prepared.json.operationId : '';
  const capability = typeof prepared.json?.capability === 'string' ? prepared.json.capability : '';
  if (prepared.status !== 200 || operationId === '' || capability === '') return { kind: 'unavailable', status: prepared.status };

  const anon = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
  const committed = await call({ action: 'commit', operationId, capability }, anon);
  let outcome: DeletionOutcome;
  if (committed.kind === 'answered' && committed.status === 200 && committed.json?.status === 'committed') outcome = { kind: 'committed' };
  else if (committed.kind === 'answered' && (committed.status === 403 || committed.status === 409)) outcome = { kind: 'denied' };
  else {
    // Transport loss or an ambiguous answer: ask the server once instead of guessing.
    const status = await call({ action: 'status', operationId, capability }, anon);
    outcome = status.kind === 'answered' && status.status === 200 && status.json?.status === 'committed' ? { kind: 'committed' } : { kind: 'unknown' };
  }
  if (outcome.kind === 'committed') await signOutAction();
  return outcome;
}
